import * as React from "react";
import { ResponsiveLine, Serie, PointTooltip, Datum } from "@nivo/line";
import * as Moment from "moment";
import DateRangeModal from "../../misc/components/queryTable/DateRangeModal";
import { CartesianMarkerProps } from "@nivo/core";
import { maxBy, minBy } from "../../misc/ArrayFunctions";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";

interface DiaryTrendGraphProps {
    data: Serie[];
    markers?: CartesianMarkerProps[];
    onPointClick: (e) => void;
    showFilter: boolean;
    onShowFilter: (show: boolean) => void;
    tooltip: PointTooltip;
    yAxisLabel: string;
    baseline?: number;
}

export const DiaryTrendGraph = ({
    data,
    markers,
    onPointClick,
    showFilter,
    onShowFilter,
    tooltip,
    yAxisLabel,
    baseline
}: DiaryTrendGraphProps) => {
    const [minDate, setMinDate] = React.useState(
        Moment.min(...data[0].data.map(d => Moment.parseZone(d.x).startOf("day")))
    );
    const [maxDate, setMaxDate] = React.useState(
        Moment.max(...data[0].data.map(d => Moment.parseZone(d.x).endOf("day")))
    );

    const dataPoints = data.map(d => ({
        ...d,
        data: d.data.filter(
            s => (!minDate.isValid() || Moment.parseZone(s.x).isSameOrAfter(minDate)) && (!maxDate.isValid() || Moment.parseZone(s.x).isSameOrBefore(maxDate))
        )
    })).filter(e => e.data.length != 0);

    const XTickValues = (values: Datum[]): string[] => {
        if (values.length === 0) return [];

        if (values.length < 20) return values.map(d => Moment.parseZone(d.x).format("YYYY-MM-DD"));

        const mondays = values
            .filter(d => Moment.parseZone(d.x).day() === 1)
            .map(d => Moment.parseZone(d.x).format("YYYY-MM-DD"));

        if (mondays.length < 20) return mondays;

        const firstOfMonth = values
            .filter(d => Moment.parseZone(d.x).date() === 1)
            .map(d => Moment.parseZone(d.x).format("YYYY-MM-DD"));

        if (firstOfMonth.length < 20) return firstOfMonth;

        const quarters = values
            .filter(d => Moment.parseZone(d.x).date() === 1 && [0, 3, 6, 9].includes(Moment.parseZone(d.x).month()))
            .map(d => Moment.parseZone(d.x).format("YYYY-MM-DD"));

        return quarters;
    };

    const onFilterApplied = (start: string, end: string) => {
        setMinDate(Moment.parseZone(start));
        setMaxDate(Moment.parseZone(end));
    };

    const onClearFilters = () => {
        setMinDate(Moment.min(...data[0].data.map(d => Moment.parseZone(d.x))));
        setMaxDate(Moment.max(...data[0].data.map(d => Moment.parseZone(d.x))));

        onShowFilter(false);
    };

    const XTickRenderer = ({
        opacity,
        textBaseline,
        textX,
        textY,
        value,
        textAnchor,
        x,
        y
    }: any) => {
        return (
            <g transform={`translate(${x},${y})`} style={{ opacity }}>
                <text
                    alignmentBaseline={textBaseline}
                    textAnchor={textAnchor}
                    transform={`translate(${textX},${textY})`}
                >
                    {Moment.parseZone(value).format("DD-MMM-YYYY")}
                </text>
            </g>
        );
    };

    const YTickRenderer = ({
        opacity,
        textBaseline,
        textX,
        textY,
        value,
        textAnchor,
        x,
        y
    }: any) => {
        return (
            <g transform={`translate(${x},${y})`} style={{ opacity }}>
                <text
                    alignmentBaseline={textBaseline}
                    textAnchor={textAnchor}
                    transform={`translate(${textX},${textY})`}
                >
                    {value}
                </text>
            </g>
        );
    };


    const minDataPointY = dataPoints[0] ? minBy(dataPoints[0].data, 'y') : 0;
    const maxDataPointY = dataPoints[0] ? maxBy(dataPoints[0].data, 'y') : 0;

    const minY = baseline < minDataPointY.y ? baseline : "auto";
    const maxY = baseline > maxDataPointY.y ? baseline : "auto";

    return (
        <>
            {
                dataPoints.length == 0 ?
                    <Alert show={true} category={AlertCategoryEnum.Info}>
                        There is no data to trend
                    </Alert>
                    :
                    <div style={{ height: 400 + "px" }}>
                        <ResponsiveLine
                            data={dataPoints}
                            margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                            xScale={{ type: "point" }}
                            yScale={{
                                type: "linear",
                                min: minY,
                                max: maxY,
                                stacked: true,
                                reverse: false
                            }}
                            xFormat="time:%Y-%m-%d"
                            yFormat=" >-.2f"
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                tickValues: XTickValues(dataPoints[0].data),
                                legend: "Date / Time",
                                legendOffset: 36,
                                legendPosition: "middle",
                                renderTick: dt => XTickRenderer(dt)
                            }}
                            axisLeft={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: yAxisLabel,
                                legendOffset: -50,
                                legendPosition: "middle",
                                renderTick: dt => YTickRenderer(dt)
                            }}
                            pointSize={10}
                            pointBorderWidth={2}
                            tooltip={tooltip}
                            onClick={(e) => {
                                onPointClick(e)
                            }}
                            useMesh={true}
                            markers={markers}
                            legends={[
                                {
                                    anchor: "bottom-right",
                                    direction: "column",
                                    justify: false,
                                    translateX: 100,
                                    translateY: 0,
                                    itemsSpacing: 0,
                                    itemDirection: "left-to-right",
                                    itemWidth: 80,
                                    itemHeight: 20,
                                    itemOpacity: 0.75,
                                    symbolSize: 12,
                                    symbolShape: "circle",
                                    symbolBorderColor: "rgba(0, 0, 0, .5)",
                                    effects: [
                                        {
                                            on: "hover",
                                            style: {
                                                itemBackground: "rgba(0, 0, 0, .03)",
                                                itemOpacity: 1
                                            }
                                        }
                                    ]
                                }
                            ]}
                        />
                    </div>
            }
            <DateRangeModal
                header="Trend Filter"
                show={showFilter}
                to={maxDate.format("YYYY-MM-DD")}
                from={minDate.format("YYYY-MM-DD")}
                onChange={onFilterApplied}
                onClear={onClearFilters}
                onApply={() => onShowFilter(false)}
                onClose={() => onShowFilter(false)}
            />
        </>
    );
};
